/**
 * GameCard — renders one {@link GalleryItem}. EVERY card links same-window to
 * `/g/:slug` (PR-H decision 4): creations and hosted natives play there, and
 * external natives (IceSpace) get the interstitial whose "Open ↗" button keeps
 * the new-tab external launch — so the gallery always stays one Back away.
 */
import { Link } from 'react-router-dom';
import type { GalleryItem } from '../gallery/model.js';
import { lastUpdatedLabel, isNewRelease, isTopRated } from '../gallery/model.js';
import { slugGradient } from '../gallery/cover.js';

function StatusDot({ status }: { status: string }) {
  return <span className={`status-dot status-dot--${status}`} title={status} />;
}

export function GameCard({ item, editHref }: { item: GalleryItem; editHref?: string }) {
  const initial = item.title.charAt(0).toUpperCase();
  // Only creations carry an updatedAt; natives are evergreen catalog entries.
  const updated = item.kind === 'creation' ? lastUpdatedLabel(item.updatedAt) : null;

  const artworkUrl = item.coverUrl || item.posterUrl;

  // The die-cut sticker badges overhanging the tile's top edge (spec `.stk`):
  // an Editors' Pick mark, a fresh first publish, a forked-from game. The
  // laurel stamp below says the same "pick" thing in longhand and stays — the
  // e2e contract (card-laurel) reads it.
  const pick = !!item.editorsPick?.everSelected;
  const fresh = isNewRelease(item);
  const remixes = item.kind === 'creation' ? item.forks : 0;

  const inner = (
    <div className={`card${editHref ? ' card--editable' : ''}`} data-slug={item.slug}>
      {pick && <span className="stk stk--pick">★ pick</span>}
      {fresh && <span className="stk stk--new">NEW!</span>}
      {remixes > 0 && (
        <span className="stk stk--remix">
          ⑂ {remixes} {remixes === 1 ? 'remix' : 'remixes'}
        </span>
      )}
      <div
        className={`card__thumb${artworkUrl ? ' card__thumb--cover' : ''}`}
        style={artworkUrl ? undefined : { background: slugGradient(item.slug) }}
      >
        {artworkUrl && (
          <img className="card__cover" src={artworkUrl} alt="" loading="lazy" aria-hidden="true" />
        )}
        {item.kind === 'native' && <span className="card__native-flag">native</span>}
        {item.editorsPick?.everSelected && (
          <span
            className={`card__laurel${item.editorsPick.current ? ' card__laurel--current' : ''}`}
            data-testid="card-laurel"
            title={
              item.editorsPick.current
                ? "Current Editors' Pick — Official Selection"
                : `Editors' Pick — Official Selection${
                    item.editorsPick.selectionCount > 1
                      ? ` (${item.editorsPick.selectionCount}× selected)`
                      : ''
                  }`
            }
          >
            <img src="/editors-pick/laurel.webp" alt="" aria-hidden="true" />
            <span className="card__laurel-label">
              {item.editorsPick.current ? "Editors' Pick" : 'Selection'}
              {item.editorsPick.selectionCount > 1 ? ` ×${item.editorsPick.selectionCount}` : ''}
            </span>
          </span>
        )}
        {isTopRated(item) && (
          <span
            className="card__rated"
            data-testid="card-rated"
            title={`Top rated — scored ${item.kind === 'creation' ? item.rating?.score : ''}/100 on a rendered playtest of the live build`}
          >
            ★ Top rated
          </span>
        )}
        {!artworkUrl && initial}
      </div>
      <div className="card__body">
        <div className="card__title">{item.title}</div>
        <div className="card__desc">{item.description}</div>
        {updated && (
          <div className="card__updated" data-testid="card-updated">
            Updated {updated}
          </div>
        )}
        <div className="card__meta">
          <span className="style-badge">{item.style}</span>
          <span>@{item.creatorHandle}</span>
          <span className="header__spacer" />
          {item.kind === 'creation' ? (
            <>
              <StatusDot status={item.status} />
              <span>{item.plays} plays</span>
              <span>{item.views} views</span>
              <span>{item.likes} ♥</span>
              <span data-testid="card-forks">⑂ {item.forks}</span>
              {item.version > 0 && <span>v{item.version}</span>}
            </>
          ) : (
            // Natives carry real counters too (their docs back the game page's
            // social surface) — always shown, zeros included. No fork count
            // (platform games cannot be forked), no build status/version.
            <>
              <span>{item.plays} plays</span>
              <span>{item.views} views</span>
              <span>{item.likes} ♥</span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Keep the ordinary card itself as the gallery link. Editable cards need a
  // wrapper so their separate Studio link is never nested inside that anchor.
  if (!editHref) {
    return (
      <Link to={`/g/${item.slug}`} className="card-link" data-testid="card-link" data-slug={item.slug}>
        {inner}
      </Link>
    );
  }
  return (
    <div className="card-link card-link--editable" data-testid="card-link" data-slug={item.slug}>
      <Link to={`/g/${item.slug}`} className="card-link__game" aria-label={`View ${item.title}`}>
        {inner}
      </Link>
      <Link to={editHref} className="btn btn--sm card__edit" data-testid="edit-game">
        Edit
      </Link>
    </div>
  );
}
